import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
    ActivatedRouteSnapshot,
    NavigationEnd,
    ResolveEnd,
    Router,
    RouterModule,
} from '@angular/router';
import { ToastComponent } from '@app/components/alerts/toast/toast.component';
import { PageHeaderComponent } from '@app/components/page-header/page-header.component';
import { SessionTimeOutComponent } from '@app/components/session-time-out/session-time-out.component';
import { APP_DEFAULTS } from '@app/constants/app-defaults';
import { STORAGE_SETTING } from '@app/constants/local-storage-setting';
import { AppInsightsService } from '@app/services/app-insights/app-insights.service';
import { AppStore } from '@app/stores/app/app.store';
import { environment } from '@env/environment';
import { IPageViewTelemetry } from '@microsoft/applicationinsights-web';
import { TranslationService } from '@my-ul/tod-angular-client';
import { filter, Subscription } from 'rxjs';
import { BusyProgressComponent } from '../../components/busy-progress/busy-progress.component';
@Component({
    selector: 'app-main-shell',
    imports: [
        RouterModule,
        ToastComponent,
        BusyProgressComponent,
        PageHeaderComponent,
        SessionTimeOutComponent,
    ],
    templateUrl: './main-shell.component.html',
    styleUrls: ['./main-shell.component.scss'],
})
export class MainShellComponent implements OnInit, OnDestroy {
    private tod = inject(TranslationService);
    private appInsights = inject(AppInsightsService);
    private appStore = inject(AppStore);
    private subs$ = new Subscription();
    private iconRegistry = inject(MatIconRegistry);
    private sanitizer = inject(DomSanitizer);
    private router = inject(Router);
    isMF = this.appStore.isMF;

    ngOnInit(): void {
        this.initAppInsights();
        this.initLogLocationHistory();
        this.registerIcons();

        const lang =
            sessionStorage.getItem(STORAGE_SETTING.Lang) ?? environment.defaultLanguage;
        this.appStore.setLanguageSelected({ value: lang });
        this.subs$.add(
            this.tod.getLabels(lang).subscribe((labels) => {
                this.appStore.setTranslations({
                    value: labels as typeof APP_DEFAULTS.i18n,
                });
            }),
        );
    }

    ngOnDestroy(): void {
        this.subs$.unsubscribe();
    }

    registerIcons() {
        const {
            DESCENDING_ICON,
            ASCENDING_ICON,
            SORT_ICON,
            COPY_ICON,
            INFO_ICON,
            EXPORT_ICON,
            DESCRIPTION_ICON,
            HISTORY_ICON,
            DOWNLOAD_ICON,
            IRON_STEAM_ICON,
            DRYCLEAN_ICON,
            BLEACH_ICON,
            MACHINE_WASH_ICON,
        } = APP_DEFAULTS.IconRegistry;

        const icons: Record<string, string> = {
            'descend': DESCENDING_ICON,
            'ascend': ASCENDING_ICON,
            'new-sort': SORT_ICON,
            'copy': COPY_ICON,
            'info': INFO_ICON,
            'export': EXPORT_ICON,
            'description': DESCRIPTION_ICON,
            'history': HISTORY_ICON,
            'download': DOWNLOAD_ICON,
            'iron-steam': IRON_STEAM_ICON,
            'dryclean': DRYCLEAN_ICON,
            'bleach': BLEACH_ICON,
            'machine-wash': MACHINE_WASH_ICON,
        };

        Object.entries(icons).forEach(([name, icon]) => {
            this.iconRegistry.addSvgIconLiteral(
                name,
                this.sanitizer.bypassSecurityTrustHtml(icon),
            );
        });
    }

    initLogLocationHistory() {
        this.subs$.add(
            this.router.events
                .pipe(
                    filter(
                        (event: unknown): event is NavigationEnd =>
                            event instanceof NavigationEnd,
                    ),
                )
                .subscribe((event: NavigationEnd) => {
                    const prevUrl = this.appStore.locationHistory.currentUrl();
                    this.appStore.setLocationHistory({
                        value: {
                            prevUrl: prevUrl,
                            currentUrl: event.url,
                        },
                    });
                }),
        );
    }

    initAppInsights() {
        if (!environment.appInsights.enableTracking) return;
        this.subs$.add(
            this.router.events
                .pipe(
                    filter(
                        (event: unknown): event is ResolveEnd =>
                            event instanceof ResolveEnd,
                    ),
                )
                .subscribe((event: ResolveEnd) => {
                    const activatedComponent = this.getActivatedComponent(
                        event.state.root,
                    );
                    if (activatedComponent) {
                        this.appInsights.logPageView({
                            name: activatedComponent.name,
                            uri: event.urlAfterRedirects,
                        });
                    }
                }),
        );
    }

    private getActivatedComponent(
        snapshot: ActivatedRouteSnapshot,
    ): IPageViewTelemetry | null {
        if (snapshot.firstChild) {
            return this.getActivatedComponent(snapshot.firstChild);
        }
        return snapshot.component;
    }
}
